import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import { createLeadSource, reset } from 'store/modules/lead';
import useQuery from '../../../modules/react/hooks/useQuery';
import useMutation from '../../../modules/react/hooks/useMutation';
import PopupBuilder from '../../../website/components/stateful/Popup/Builder';

export const getFilenameFromFeaturedImage = filename => (filename ? filename.split('/').pop() : '');

export const UPLOAD_MEDIUM = gql`
  mutation MediumUpload($file: Upload!) {
    mediumUpload(file: $file) {
      medium {
        id
        companyId
        filename
        url
        formattedUpdatedAt
        defaultMetadata {
          title
          altText
        }
      }
      userErrors {
        code
        message
      }
    }
  }
`;

export const UPDATE_MEDIUM = gql`
  mutation MediumUpdate($id: ID!, $input: MediumInput!) {
    mediumUpdate(id: $id, input: $input) {
      userErrors {
        code
        message
      }
    }
  }
`;

export const GET_MEDIUM = gql`
  query GetMedium($id: ID, $filename: String) {
    medium(id: $id, filename: $filename) {
      id
      filename
      url
      placeholder
      srcSet
      sourceWidth
      sourceHeight
      trash
      defaultMetadata {
        title
        altText
      }
      metadata {
        title
        altText
      }
    }
  }
`;

const propTypes = {
  savedWidget: PropTypes.shape({
    payload: PropTypes.shape({
      mediumId: PropTypes.string,
      popupHeaderText: PropTypes.string,
      popupBodyContent: PropTypes.object,
      popupIncludeForm: PropTypes.bool,
      popupButtonText: PropTypes.string,
      thankYouHeaderText: PropTypes.string,
      thankYouBodyContent: PropTypes.object,
      thankYouButtonText: PropTypes.string,
      settingsTrigger: PropTypes.string,
      settingsTimer: PropTypes.string,
      settingsMobile: PropTypes.bool,
      settingsLeadSource: PropTypes.string,
      settingsNewLeadSource: PropTypes.string,
    }).isRequired,
  }),
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
    }),
  ),
  show: PropTypes.bool.isRequired,
  save: PropTypes.func.isRequired,
  delete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  resetLeadStore: PropTypes.func.isRequired,
  createSource: PropTypes.func.isRequired,
  isCreatingLeadSource: PropTypes.bool.isRequired,
  createdLeadSource: PropTypes.bool.isRequired,
  errorCreatingLeadSource: PropTypes.string.isRequired,
  isSavingPopup: PropTypes.bool.isRequired,
};

const defaultProps = {
  sources: [],
  savedWidget: {
    payload: {},
  },
};

const initialState = ({ payload = {} }) => ({
  saved: Object.getOwnPropertyNames(payload).length > 0,
  payload: {
    mediumId: '',
    featuredImage: '',
    popupImageAltText: '',
    popupImageTitleText: '',
    popupHeaderText: 'My Header Text',
    popupBodyContent: {
      blocks: [
        {
          key: 'dl71j',
          text: 'My body text.',
          type: 'unstyled',
          depth: 0,
          inlineStyleRanges: [],
          entityRanges: [],
          data: {},
        },
      ],
      entityMap: {},
    },
    popupIncludeForm: true,
    popupButtonText: 'Submit',
    thankYouHeaderText: 'Thank You!',
    thankYouBodyContent: {
      blocks: [
        {
          key: 'dl71j',
          text: 'My body text.',
          type: 'unstyled',
          depth: 0,
          inlineStyleRanges: [],
          entityRanges: [],
          data: {},
        },
      ],
      entityMap: {},
    },
    thankYouButtonText: 'Close',
    settingsTrigger: 'scroll',
    settingsTimer: '0',
    settingsMobile: true,
    settingsLeadSource: '-1',
    settingsNewLeadSource: '',
    errorCreatingLeadSource: '',
    ...payload,
  },
});

const PopupBuilderContainer = ({
  savedWidget,
  sources,
  show,
  save,
  delete: del,
  onClose,
  resetLeadStore,
  createSource,
  isCreatingLeadSource,
  createdLeadSource,
  errorCreatingLeadSource,
  isSavingPopup,
}) => {
  const previousCreatedLeadSource = useRef(createdLeadSource);

  const [uploadImage, { loading, data: uploadData }] = useMutation(UPLOAD_MEDIUM);
  const [updateMedium] = useMutation(UPDATE_MEDIUM, { refetchQueries: ['GetAllMedia'] });

  const [state, setState] = useState(initialState(savedWidget || {}));
  const [error, setError] = useState();

  const { data: mediumData } = useQuery(GET_MEDIUM, {
    variables: {
      id: state?.payload?.mediumId,
      filename: getFilenameFromFeaturedImage(state?.payload?.featuredImage),
    },
  });

  useEffect(() => {
    if (isCreatingLeadSource) {
      return;
    }

    resetLeadStore();
  }, []);

  useEffect(() => {
    if (uploadData && uploadData.mediumUpload.medium) {
      onFeaturedImageChange(uploadData.mediumUpload.medium);
    } else if (uploadData && uploadData.mediumUpload.userErrors.length) {
      setError(uploadData.mediumUpload.userErrors.map(err => err.message));
    }
  }, [uploadData]);

  useEffect(() => {
    const { payload } = state;

    if (previousCreatedLeadSource.current || !createdLeadSource) {
      return;
    }

    const { settingsNewLeadSource, ...rest } = payload;
    const payloadWithNewLeadSource = {
      ...rest,
      settingsLeadSource: payload.settingsNewLeadSource,
    };

    save('PopUp', payloadWithNewLeadSource);
    onCloseModal();
  }, [createdLeadSource]);

  const onUpload = ({ target: { files: [file] } }) => {
    if (!file) { return; }
    uploadImage({ variables: { file } });
  };

  const onTextEditorFinish = ({ name, rawContentState }) => (
    setPayload({ [name]: rawContentState })
  );

  const onFeaturedImageChange = (data = null) => {
    if (!data) {
      return;
    }

    const { id: mediumId } = data;
    setPayload({ mediumId: typeof mediumId === 'string' ? mediumId : '' });
  };

  const onRemoveImage = () => {
    setPayload({ mediumId: '' });
    // setPayload({ popupImageAltText: '' });
    // setPayload({ popupImageTitleText: '' });
  };

  const altTextControlChange = (arg) => {
    setError();
    onControlChange(arg);
  };

  const onControlChange = ({ target: { name, value } }) => (
    setPayload({ [name]: value })
  );

  const onCheckboxChange = ({ target: { name, checked } }) => (
    setPayload({ [name]: checked })
  );

  const onDeletePopup = () => {
    del('PopUp');
    onCloseModal();
  };

  const onSavePopup = async (event) => {
    event.preventDefault();
    if (event.target.tagName.toLowerCase() === 'button') {
      const { payload } = state;

      if (isCreatingLeadSource) {
        return;
      }

      if (payload.settingsLeadSource !== 'Create new lead source') {
        await updateMedium({ variables: { id: payload.mediumId,
          input: {
            defaultMetadata: {
              locale: 'en-us',
              altText: payload.popupImageAltText,
              title: payload.popupImageTitleText,
            },
          },
        } });
        save('PopUp', payload);
        onCloseModal();
        return;
      }

      createSource(payload.settingsNewLeadSource);
    }
  };

  const onCloseModal = () => {
    setState(initialState);
    onClose('PopUp');
  };

  const setPayload = value => (
    setState({
      payload: {
        ...state.payload,
        ...value,
      },
    })
  );

  const setMedium = (medium) => {
    setPayload({
      mediumId: medium.id,
      popupImageAltText: medium.defaultMetadata.altText,
      popupImageTitleText: medium.defaultMetadata.title,
    });
  };

  return (
    <PopupBuilder
      medium={mediumData?.medium}
      {...state.payload}
      show={show}
      saved={state.saved}
      sources={sources}
      isSavingPopup={isSavingPopup}
      isCreatingLeadSource={isCreatingLeadSource}
      errorCreatingLeadSource={errorCreatingLeadSource}
      onSavePopup={onSavePopup}
      onCloseModal={onCloseModal}
      onDeletePopup={onDeletePopup}
      altTextControlChange={altTextControlChange}
      onControlChange={onControlChange}
      onCheckboxChange={onCheckboxChange}
      onTextEditorFinish={onTextEditorFinish}
      onFeaturedImageChange={onFeaturedImageChange}
      onRemoveImage={onRemoveImage}
      loading={loading}
      onUpload={onUpload}
      setFormError={setError}
      formError={error}
      setMedium={setMedium}
    />
  );
};

PopupBuilderContainer.propTypes = propTypes;
PopupBuilderContainer.defaultProps = defaultProps;

const mapStateToProps = state => ({
  isCreatingLeadSource: state.lead.creatingSource,
  createdLeadSource: state.lead.createdSource,
  isSavingPopup: state.pageBuilder.saving,
  errorCreatingLeadSource: state.lead.sourceErrorMessage,
});

const mapDispatchToProps = {
  createSource: createLeadSource,
  resetLeadStore: reset,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PopupBuilderContainer);
